import type { Dict } from "./lv.ts";

export const en: Dict = {
  "app.title": "Stundio",
  "general.continue": "Continue",

  "nav.home": "Home",
  "nav.week": "Week",
  "nav.changes": "Changes",
  "nav.subjects": "Subjects",
  "nav.transit": "Transit",
  "nav.settings": "Settings",
  "nav.group.schedule": "Schedule",
  "nav.group.personal": "Personal",

  "onboarding.title": "Choose your class",
  "onboarding.subtitle": "Timetable and changes — even offline.",

  "onboarding.persona.title": "Choose your side",
  "onboarding.persona.subtitle": "Tailor the schedule to your daily role",
  "onboarding.persona.student.title": "Student",
  "onboarding.persona.student.desc": "See your class lessons and daily substitutions",
  "onboarding.persona.teacher.title": "Teacher",
  "onboarding.persona.teacher.desc": "See your teaching day, rooms, and cover duties",

  "onboarding.teacher.title": "Select teacher",
  "onboarding.teacher.subtitle": "Search by first or last name",

  "teacher.search": "Search teacher…",
  "teacher.none": "No teacher found",
  "teacher.formClass": "Form teacher",
  "teacher.cover": "Cover",
  "teacher.coverFor": "Covering: {name}",
  "teacher.view.own": "My lessons",
  "teacher.view.formClass": "My class",

  "onboarding.language.title": "Choose your language",
  "onboarding.language.continue": "Continue",

  "onboarding.intro.skip": "Skip",
  "onboarding.intro.next": "Next",
  "onboarding.intro.start": "Get started",
  "onboarding.intro.progress": "Intro step",
  "onboarding.intro.step1.title": "Your timetable, in one place",
  "onboarding.intro.step1.body": "Day and week views, laid out clearly — like EduPage, but faster.",
  "onboarding.intro.step2.title": "Changes show up right away",
  "onboarding.intro.step2.body":
    "Cancelled, moved, and substituted lessons are flagged — nothing slips by.",
  "onboarding.intro.step3.title": "Works offline too",
  "onboarding.intro.step3.body":
    "The timetable stays on your device, so it's there even without a connection.",
  "onboarding.intro.step4.title": "Notified about what matters",
  "onboarding.intro.step4.body":
    "A heads-up before class, and a ping if substitutions change — nothing else.",

  "class.search": "Search classes…",
  "class.none": "No classes found",
  "class.favorites": "Favorites",
  "class.all": "All classes",
  "class.loading": "Loading class list…",
  "class.favorite.add": "Add to favorites",
  "class.favorite.remove": "Remove from favorites",

  "subgroup.title": "Which {class} subgroup are you in?",
  "subgroup.subtitle": "This class is split into subgroups — pick yours to see only your lessons.",
  "subgroup.option": "Subgroup {label}",
  "subgroup.skip": "Skip — show both subgroups",

  "day.today": "Today",
  "day.tomorrow": "Tomorrow",
  "day.yesterday": "Yesterday",
  "day.jumpToday": "Go to today",
  "day.previousDay": "Previous day",
  "day.nextDay": "Next day",
  "day.openCalendar": "Choose a date",
  "day.previousMonth": "Previous month",
  "day.nextMonth": "Next month",
  "day.pageHint": "Swipe left or right, or use the arrow keys, to change day",
  "day.empty": "No lessons",
  "day.emptyHint": "A holiday, maybe?",
  "day.noClass": "Pick a class first",
  "day.changeClass": "Change class",
  "day.noData": "Nothing cached yet",
  "day.noDataHint": "Go online once to download the timetable.",
  "week.longBreak": "Long break · {min} min",
  "week.noSchedule": "No timetable for this week yet",
  "week.noScheduleHint": "It usually appears closer to the date. Pull down to check for updates.",
  "day.stale": "This week's timetable is not published yet — showing the previous one.",
  "day.offline": "No internet connection — showing the saved timetable.",
  "day.buildingOther": "Different building: {building}",
  "day.buildingMixed": "Several buildings: {buildings}",
  "home.schedule": "Schedule for the day",
  "home.notifyBlocked": "Notifications are off — you won't get lesson reminders or change alerts.",
  "home.notifyBlockedWeb":
    "Notifications are blocked in the browser — allow them in the site settings to get alerts.",
  "day.notes": "Announcements",
  "day.allNotes": "All school announcements ({count})",
  "day.onlyMyGroup": "Only for my group",
  "day.free": "Free period",
  "day.finished": "Lessons are over",
  "day.now": "Now",
  "day.glance.left": "{duration} left",
  "day.glance.in": "In {duration}",
  "day.glance.following": "Next",
  "day.glance.walk": "Head to {building}",
  "day.glance.walkFrom": "Walk from {from} to {building}",
  "day.summary.lessons": "Lessons: {count}",
  "day.summary.free": "Free: {duration}",
  "day.summary.lessonsTooltip": "Total number of lessons today",
  "day.summary.freeTooltip": "Total break time between lessons",
  "day.tomorrow.eyebrow": "Next school day · {weekday}",
  "day.tomorrow.starts": "Starts {time}",
  "day.tomorrow.changes": "Changes: {count}",
  "day.tomorrow.open": "Open {weekday}: starts {time}",
  "day.glance.open": "Open {title}, {countdown}",
  "day.feedbackPrompt.title": "Got an idea?",
  "day.feedbackPrompt.body": "Tell us what to add or fix in Stundio.",
  "day.feedbackPrompt.action": "Suggest",
  "day.feedbackPrompt.dismiss": "Dismiss",
  "day.androidBanner.title": "Stundio for Android",
  "day.androidBanner.body":
    "Download the app for home-screen widgets, background sync, and offline schedule.",
  "day.androidBanner.action": "Download APK",
  "day.androidBanner.dismiss": "Dismiss",
  "day.updatePrompt.title": "Update available",
  "day.updatePrompt.body": "A newer version of Stundio is available ({version}).",
  "day.updatePrompt.action": "Update",
  "day.updatePrompt.dismiss": "Later",
  "day.updatePrompt.handoff":
    "Download finished. Confirm the installation in the Android window, or tap Update to try again.",
  "day.changesBadge": "{n} changes",

  "changes.title": "Changes",
  "changes.filter.myClass": "My group",
  "changes.filter.myChanges": "My changes",
  "changes.filter.all": "All classes",
  "changes.absentTeachers": "Absent today",
  "changes.emptyForClass": "No changes",
  "changes.emptyForClassHint": "Lessons for your group follow the regular schedule today",
  "changes.emptyAll": "No registered changes",
  "changes.emptyAllHint": "The school has not published any changes for this day",
  "changes.otherGroupsHaveChanges": "There are {n} changes for other groups today",
  "changes.viewOtherGroups": "View all changes",
  "changes.searchPlaceholder": "Search group, subject, or teacher…",
  "changes.searchNoResults": "Nothing found",
  "changes.count": "{n} changes",
  "changes.badge": "{n} changes",
  "changes.notesFromSchool": "School announcements",
  "changes.rvtWebsite": "RVT website",

  "week.previousWeek": "Previous week",
  "week.nextWeek": "Next week",
  "week.buildingDays": "{building}: {days}",
  "week.overview.title": "Overview",
  "week.overview.lessons": "{n} lessons",
  "week.overview.lessonsUp": "+{n} vs last week",
  "week.overview.lessonsDown": "{n} vs last week",
  "week.overview.lessonsSame": "Same as last week",
  "week.overview.changes": "{n} changes",
  "week.overview.changesNone": "No changes",
  "week.overview.busiestLabel": "Busiest day",
  "week.overview.lightestLabel": "Lightest day",

  "share.week": "Share this week",
  "share.working": "Making the image…",
  "share.error": "Could not create the image",
  "share.title": "{name} timetable",
  "share.message":
    "{name} timetable {period} \nWant to have better experience with EduPage, check out: https://bit.ly/stundio",
  "share.image.classTeacher": "Form teacher",
  "share.image.appLabel": "Better EduPage experience on phone",
  "share.language.title": "Which language should the share be in?",
  "share.language.sync": "Synchronize the share image with my app language",
  "share.language.continue": "Continue",

  "lesson.teacher": "Teacher",
  "lesson.room": "Room",
  "lesson.period": "Period",
  "lesson.group": "Group",
  "lesson.building": "Building",
  "lesson.was": "Was",
  "lesson.fromSchool": "From school",
  "lesson.note": "Announcement",
  "lesson.close": "Close",

  "status.cancelled": "Cancelled",
  "status.moved": "Moved",
  "status.substituted": "Substituted",
  "status.room_change": "Room changed",
  "status.added": "Extra lesson",

  "sync.syncing": "Updating…",
  "sync.offline": "Offline",
  "sync.error": "Update failed",
  "sync.never": "Never updated",
  "sync.updated": "Updated {time}",
  "sync.refresh": "Refresh",
  "sync.pull": "Pull to refresh",
  "sync.release": "Release to refresh",
  "sync.toast": "Up to date",

  "time.inMinutes": "in {n} min",
  "time.minutesLeft": "{n} min left",
  "time.startsNow": "starting now",

  "subjects.title": "Subjects",
  "transit.title": "Transit",
  "transit.plan.title": "Plan my trip",
  "transit.plan.description":
    "Enter your starting point and destination. Open the route in Google Maps.",
  "transit.plan.home": "Starting point",
  "transit.plan.homePlaceholder": "Home address",
  "transit.plan.useLocation": "Use my current location",
  "transit.plan.savedHome": "Starting point from Settings: {address}",
  "transit.plan.homeMissing": "Add your home address in Settings or use your current location.",
  "transit.plan.destination": "Destination",
  "transit.plan.destinationPlaceholder": "Address or place name",
  "transit.plan.transitDuration": "Transit and transfers (minutes)",
  "transit.plan.walkDuration": "Walk to the stop (minutes)",
  "transit.plan.nextLesson": "Next lesson: {subject} at {time}",
  "transit.plan.class": "lesson",
  "transit.plan.leaveAt": "Leave around {time}",
  "transit.plan.leaveHint": "Includes your walk-to-stop and transit time estimates.",
  "transit.plan.noLesson": "No more lessons are scheduled for your selected group today.",
  "transit.plan.findRoute": "Find public transport route",
  "transit.plan.destinationNeeded": "Enter a destination to find a route.",
  "transit.plan.mapsNote": "Google Maps will show available routes and departure times.",
  "transit.plan.mapsKeyNeeded": "The embedded Google Maps view needs app configuration.",
  "transit.plan.mapTitle": "Public transport route in Google Maps",
  "transit.plan.goToSchool": "To school",
  "transit.plan.goHome": "Go home",
  "transit.plan.schoolDay": "First lesson at {start} · {building} · last lesson ends at {end}",
  "transit.plan.main": "the main building",
  "transit.plan.tic": "the Technology and Innovation Centre",
  "transit.plan.startsAtSeven": "Routes to school will appear from 7:00 AM.",
  "transit.plan.fromTo": "Route",
  "transit.plan.currentLocation": "Your location",
  "transit.plan.homeAddress": "Home ({address})",
  "transit.plan.homeward": "home",
  "transit.plan.noHome": "home address is not set",
  "transit.plan.refreshLocation": "Refresh GPS",
  "transit.plan.refreshRoutes": "Refresh routes",
  "transit.plan.lessonsAtTic": "Your lessons are at the Technology and Innovation Centre.",
  "transit.plan.alreadyAtSchool": "You are already at school.",
  "transit.plan.loadingRoutes": "Finding public transport routes…",
  "transit.plan.routesKeyNeeded": "Add a Google Routes API key to the app to show routes.",
  "transit.plan.routesUnavailable": "Routes could not be loaded right now. Try again shortly.",
  "transit.plan.noRoutes": "No public transport connections were found for this route.",
  "transit.plan.option": "Option {number}",
  "transit.plan.leaveAround": "Leave home around {time}",
  "transit.plan.walk": "Walk",
  "transit.plan.locationUnavailable": "Location is unavailable on this device.",
  "transit.plan.locationLoading": "Getting your location…",
  "transit.plan.locationReady": "Using your current location for this route.",
  "transit.plan.locationDenied": "Could not get your location. Check your browser permission.",
  "transit.live.title": "Riga transit live",
  "transit.live.description":
    "See public transport vehicles on the map and live arrival times at stops.",
  "transit.live.show": "Show live map",
  "transit.live.source": "Live map from Rīgas satiksme",
  "transit.live.openFull": "Open full screen",
  "transit.live.frameTitle": "Rīgas satiksme live transit map",
  "transit.destinations.title": "Saved destinations",
  "transit.destinations.description": "Saved addresses are route starting points to RVT.",
  "transit.destinations.currentLocation": "From current location",
  "transit.destinations.rvtAddress": "Destination: RVT · {address}",
  "transit.destinations.openGoogleMaps": "Open Google Maps",
  "transit.destinations.nameLabel": "Place name",
  "transit.destinations.namePlaceholder": "School, work, friends…",
  "transit.destinations.addressLabel": "Address",
  "transit.destinations.addressPlaceholder": "Street, building, city",
  "transit.destinations.add": "Add destination",
  "transit.destinations.remove": "Remove {label}",
  "transit.destinations.locationUnavailable": "Location is unavailable on this device.",
  "transit.destinations.gettingLocation": "Getting your location…",
  "transit.destinations.locationDenied": "Allow location access to plan from your current location.",
  "transit.destinations.plannerTitle": "Rīgas satiksme route planner",
  "transit.destinations.plannerDescription": "Route planner opened for {destination}.",
  "transit.destinations.backToMap": "Back to map",
  "transit.live.locationNote":
    "To show your location, use the location button in the map and allow access if your browser asks.",
  "subjects.perWeek": "{n}× a week",
  "subjects.teachers": "Teachers",
  "subjects.empty": "No subjects",
  "subjects.emptyHint": "Subjects appear once the timetable is downloaded.",
  "subjects.note.placeholder": "Note for this subject…",
  "subjects.note.save": "Save",
  "subjects.note.badge": "Has a note",

  "settings.title": "Settings",
  "settings.commute": "Commute",
  "settings.commute.home": "Home address",
  "settings.commute.homePlaceholder": "Enter an address or place name",
  "settings.commute.homeHint": "Saved only on this device and used to plan your route.",
  "settings.persona.student": "Student",
  "settings.persona.teacher": "Teacher",
  "settings.identity.sheetTitle": "Choose role",
  "settings.identity.confirm": "Confirm",
  "settings.class": "My group",
  "settings.change": "Change",
  "settings.building": "Building",
  "settings.buildingAuto": "Automatic",
  "settings.subgroup": "Subgroup",
  "settings.subgroupAll": "Both",
  "settings.customization": "Customization",
  "settings.theme": "Appearance",
  "settings.language": "Language",
  "settings.share": "Sharing",
  "settings.shareLanguage": "Share image language",
  "settings.mergeLessons": "Merge identical lessons",
  "settings.mergeLessonsHint":
    "Shows a subject that repeats back-to-back in the week view as one longer block.",
  "settings.movedOnTarget": "Show moved lessons on the new day",
  "settings.movedOnTargetHint":
    "A lesson moved to another day appears on that day instead of the one it left.",
  "view.options": "View options",
  "settings.showTime": "Show time",
  "settings.showTimeHint": "Shows each lesson's start and end time next to its number.",
  "settings.notifications": "Notifications",
  "settings.showSubjectsTab": "Subjects tab",
  "settings.showSubjectsTabHint": "Shows the Subjects tab in the bottom menu, next to Settings.",
  "settings.notifyLessonReminder": "Lesson reminder",
  "settings.notifyLessonReminderHint": "Notify before the next lesson starts.",
  "settings.notifyLessonReminderOff": "Off",
  "settings.notifySubstitutionChanges": "Schedule changes",
  "settings.notifySubstitutionChangesHint": "Notify when today's or tomorrow's lessons change.",
  "settings.notifyNewTimetable": "New timetable",
  "settings.notifyNewTimetableHint": "Notify when the school publishes a new week's timetable.",
  "settings.notifyAppUpdates": "App updates",
  "settings.notifyAppUpdatesHint": "Notify when a new app version is released.",
  "settings.notifyExactAlarmDenied":
    'The system is blocking exact alarms, so reminders can arrive late. Allow "Alarms & reminders".',
  "settings.notifyPermissionDenied": "Notifications are blocked in system settings.",
  "settings.notifyOpenSettings": "Open settings",
  "settings.notificationsWebNotice":
    "Offline reminders and notifications are supported in the Android app. On the web app, schedule changes update whenever you open the app.",
  "settings.notificationsIosPwaRequired":
    "To receive notifications on iPhone, please add Stundio to your Home Screen first.",
  "settings.notificationsLessonRemindersNativeNotice":
    "Lesson countdown reminders are available in the Android app. Schedule change alerts work via Web Push notifications.",
  "notification.lessonReminder.title": "{subject} in {minutes} min",
  "notification.changed.title": "Timetable changed",
  "notification.changed.body": "Today's schedule has changes.",
  "notification.cover.title": "New cover duty",
  "notification.cover.body": "You have been assigned a cover lesson",
  "notification.newTimetable.title": "New timetable",
  "notification.newTimetable.body": "A new timetable from {date} has been published.",
  "notification.update.title": "Update available",
  "notification.update.body": "Stundio {version} is ready to download.",
  "widget.now": "Now",
  "widget.next": "Next",
  "widget.done": "No more lessons today",
  "widget.noClass": "Pick a class",
  "widget.noData": "Open Stundio to refresh",
  "widget.minutesLeft": "{minutes} min left",
  "widget.minutesUntil": "in {minutes} min",
  "settings.data": "Data",
  "settings.analytics": "Anonymous usage analytics",
  "settings.analyticsHint":
    "Helps understand which features get used. No cookies, no personal data.",
  "settings.about": "About",
  "settings.aboutText":
    "Unofficial app. Data comes from the public pikcrvt.edupage.org timetable and belongs to the school.",
  "settings.currentVersion": "Current version: {version}",
  "settings.checkForUpdatesAction": "Check",
  "settings.checkingForUpdates": "Checking…",
  "settings.upToDate": "You're on the latest version",
  "settings.updateAvailable": "A newer version is available ({version})",
  "settings.updateAction": "Download",
  "settings.updateDownloading": "Downloading… {percent}%",
  "settings.updateError": "Update failed: {message}",
  "settings.updateFailed": "Update failed",
  "whatsNew.title": "What’s new",
  "whatsNew.eyebrow": "Version {version}",
  "whatsNew.done": "Got it",
  "whatsNew.history": "Earlier versions",
  "whatsNew.empty": "No release notes yet.",
  "settings.whatsNew": "What’s new",
  "settings.whatsNewHint": "Changes in this and earlier versions",
  "settings.dangerZone": "Danger zone",
  "settings.resetData": "Reset all data",
  "settings.resetDataHint": "Deletes notes and restores default settings.",
  "settings.resetDataConfirmTitle": "Reset all data?",
  "settings.resetDataConfirmBody":
    "All notes will be deleted, and all settings will be restored to their defaults. The app will return to its initial setup.",
  "settings.resetDataConfirmButton": "Reset all data",
  "settings.resetDataCancelButton": "Cancel",
  "feedback.sheet.title": "Suggest a feature",
  "feedback.sheet.messageLabel": "Your idea or suggestion",
  "feedback.sheet.messagePlaceholder": "What should Stundio add or improve?",
  "feedback.sheet.emailLabel": "Your email (optional)",
  "feedback.sheet.emailPlaceholder": "If you would like a reply",
  "feedback.sheet.submit": "Send suggestion",
  "feedback.sheet.submitting": "Sending…",
  "feedback.sheet.successTitle": "Thank you!",
  "feedback.sheet.successBody": "Your suggestion has been sent. Thank you for your input!",
  "feedback.sheet.bugTitle": "Report a bug",
  "feedback.sheet.bugMessageLabel": "What happened?",
  "feedback.sheet.bugMessagePlaceholder":
    "Describe what went wrong and what you expected to happen…",
  "feedback.sheet.bugSubmit": "Send report",
  "feedback.sheet.bugSubmitting": "Sending…",
  "feedback.sheet.bugSuccessTitle": "Report sent!",
  "feedback.sheet.bugSuccessBody": "Thank you for letting us know! We'll look into it.",
  "feedback.sheet.close": "Close",
  "feedback.sheet.error": "Could not send. Please check your internet connection.",
  "feedback.sheet.attachScreenshot": "Attach screenshot",
  "feedback.sheet.removeScreenshot": "Remove screenshot",
  "feedback.sheet.screenshotTooLarge": "Image is too large (max 5 MB)",
  "settings.madeBy": "Made with ❤︎ by Dmytro Polizhai",

  "theme.system": "System",
  "theme.light": "Light",
  "theme.dark": "Dark",

  "customization.title": "Customization",
  "customization.theme": "Appearance",
  "customization.accent": "Accent colour",
  "customization.accent.hint": "The colour for highlights — today's date, the selected option.",
  "customization.accent.reset": "Default",
  "customization.lessonStyle": "Lesson cards",
  "customization.lessonStyle.hint": "How a subject's colour shows on the day list.",
  "customization.lessonStyle.outline": "Outline",
  "customization.lessonStyle.filled": "Filled",
  "customization.radius": "Corner radius",
  "customization.radius.compact": "Compact",
  "customization.radius.balanced": "Balanced",
  "customization.radius.standard": "Standard",
  "customization.radius.rounder": "Extra round",
  "customization.elevation": "Card depth",
  "customization.elevation.soft": "Soft",
  "customization.elevation.bold": "Bold",
  "customization.reduceMotion": "Reduce motion",
  "customization.reduceMotion.hint": "Turns off the app's transitions and animations.",
  "customization.subjectColors": "Subject colours",
  "customization.subjectColors.enabled": "Subject colour coding",
  "customization.subjectColors.enabled.hint":
    "When off, every subject shows one neutral colour on the day list and week view.",
  "customization.subjectColors.hint":
    "Tap a colour to reassign it; tap Auto to go back to the default.",
  "customization.subjectColors.reset": "Auto",
  "customization.subjectColors.custom": "Custom color",
  "customization.subjectColors.lightness": "Lightness",
  "customization.subjectColors.empty": "Pick a class to customize its subject colours.",
  "customization.subjectColors.hint.teacher":
    "These are the subjects you teach. Tap a colour to reassign it; tap Auto to go back to the default.",
  "customization.subjectColors.empty.teacher":
    "Pick yourself as a teacher to customize the colours of your subjects.",
  "customization.preview.subject": "Sample subject",
  "customization.reset": "Reset to defaults",

  "iphoneInstall.title": "Add Stundio to Home Screen",
  "iphoneInstall.subtitle":
    "Use Stundio as a full app on your iPhone — full screen, no browser bars, and instant offline access.",
  "iphoneInstall.step1": "Tap «Add to Home Screen» below or the browser Share button",
  "iphoneInstall.step2": "In the menu, select «Add to Home Screen»",
  "iphoneInstall.action": "Add to Home Screen",
  "teacherAnnouncement.title": "Stundio for Teachers",
  "teacherAnnouncement.headline": "Now available for teachers",
  "teacherAnnouncement.subtitle":
    "Teachers can now use Stundio to check their schedules, rooms and substitutions.",
  "teacherAnnouncement.step1.title": "Go to Settings",
  "teacherAnnouncement.step1.desc": "Open the Settings tab",
  "teacherAnnouncement.step2.title": "Change Profile",
  "teacherAnnouncement.step2.desc": "Select Teacher profile",
  "teacherAnnouncement.step3.title": "Pick Your Name",
  "teacherAnnouncement.step3.desc": "Find yourself in the list and get your schedule",
  "teacherAnnouncement.tryIt": "Try it now",
  "teacherAnnouncement.done": "Got it",
  "settings.teacherShare": "Teacher features",
  "settings.teacherShareHint": "Show the announcement about features for teachers",
  "iphoneInstall.dismiss": "Continue in browser",
  "settings.iphoneInstall": "Add to Home Screen",
  "settings.iphoneInstallHint": "Install Stundio as an app on your iPhone",

  "settings.group.schedule": "Schedule",
  "settings.group.appearance": "Appearance & language",
  "settings.identityHint": "Whose timetable Home, Week and the widget show.",
  "settings.subgroupHint": "Hides the other subgroup's lessons from the day and the week.",
  "settings.buildingHint": "Show the timetable of one building only, or of both.",
  "settings.themeHint": "Light, dark, or the same as your phone.",
  "settings.languageHint":
    "The app's language. Subjects and changes stay in Latvian, as on EduPage.",
  "settings.customizationHint": "Subject colours, accent, card style and corner rounding.",
  "settings.shareLanguageHint": "The language of the week image and message you share.",
  "settings.shareLangFollowApp": "Same as app",
  "settings.sync": "Sync",
  "settings.syncHint": "The timetable is stored on your phone and works offline. Tap to refresh.",
  "settings.updates": "Updates",
  "settings.updatesHint": "Checks for and installs a new version right from the app.",
  "settings.version": "Version",
  "settings.versionHint": "The app version on this device.",
  "settings.reportIssueRow": "Report a bug",
  "settings.reportIssueHint":
    "Describe the problem. The author gets it along with the app version.",
  "settings.suggestFeatureRow": "Suggest an idea",
  "settings.suggestFeatureHint": "What to add or change in the app.",
  "settings.notifyPermissionDeniedTitle": "Notifications blocked",
  "settings.notifyExactAlarmTitle": "Reminders may be late",
  "settings.notificationsWebTitle": "Notifications in the Android app",
  "settings.minutes": "{minutes} min",
  "settings.reminderOption": "{minutes} min before",

  "error.title": "Something went wrong",
  "error.hint": "Could not load this screen.",
  "error.retry": "Try again",
};
